import { useContext, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as IconCross } from 'assets/icons/svgs/icon-cross.svg';
import { UnapplyGiftCertificatePrompt } from 'components/Cart/Prompts/UnapplyGiftCertificatePrompt';
import { applyGiftCertificate } from 'services/PaymentService';
import { appConstants } from 'utils/global-constants';
import { PaymentPageContext } from 'components/Payment/PaymentPageProvider';

import './PaymentGiftCertificate.scss';

export const PaymentGiftCertificate = (props) => {
  const { isDonationOnly, giftCardsInCart } = props;

  if (isDonationOnly) {
    return null;
  }

  const { refreshOrderData } = useContext(PaymentPageContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showGiftCardForm, setShowGiftCardForm] = useState(false);
  const [giftCardButtonEnabled, setGiftCardButtonEnabled] = useState(false);
  const [giftCardApplied, setGiftCardApplied] = useState(false);
  const [giftCardError, setGiftCardError] = useState(false);
  const [giftCardInputValue, setGiftCardInputValue] = useState('');
  const [showRemoveCertificateModal, setShowRemoveCertificateModal] = useState(false);
  const [giftCertificateAmount, setGiftCertificateAmount] = useState(false);
  const checkboxRef = useRef();

  const removeGiftCertificateValues = {
    GiftCertificateNumber: giftCardInputValue,
    Amount: giftCertificateAmount,
  };

  const toggleApplyGiftCardFormHandler = (event) => {
    if (!giftCardApplied) {
      setShowGiftCardForm(event.target.checked && true);
    }
  };

  const giftCardInputHandler = (event) => {
    const giftCardInputHasAValue = event.target.value.length > 0;
    setGiftCardButtonEnabled(giftCardInputHasAValue);
    setGiftCardInputValue(event.target.value);
  };

  const removeCertificateModalHandler = (event) => {
    event.preventDefault();
    setShowRemoveCertificateModal(true);
  };

  const onApplyCertificateFromCart = (giftCardRedeemedAmount, giftCardNumber) => {
    setGiftCardError(false);
    setGiftCardApplied(true);
    setShowGiftCardForm(true);
    setGiftCardInputValue(giftCardNumber);
    setGiftCertificateAmount(giftCardRedeemedAmount);
    checkboxRef.current.checked = true;
  };

  const onUnapplyCertificate = () => {
    setGiftCardApplied(false);
    setGiftCardError(false);
    setShowGiftCardForm(false);
    setGiftCardInputValue('');
    checkboxRef.current.checked = false;
  };

  const submitGiftCardFormHandler = async () => {
    setIsProcessing(true);

    try {
      const response = await applyGiftCertificate(giftCardInputValue);
      setIsProcessing(false);

      if (response.Status === 1) {
        const giftCardRedeemedAmount = Math.abs(response.Order.Cart.RedeemedGiftCertificateTotal);
        setGiftCardError(false);
        setGiftCardApplied(true);
        setGiftCertificateAmount(giftCardRedeemedAmount);
        refreshOrderData(response.Order);
      } else if (response.ErrorMessage) {
        setGiftCardError(response.ErrorMessage);
      } else {
        setGiftCardError(appConstants.ErrorMessages.GiftCertificateCantBeAdded);
      }
    } catch (e) {
      console.error(e);
      setIsProcessing(false);
      setGiftCardError(appConstants.ErrorMessages.GiftCertificateCantBeAdded);
    }
  };

  useEffect(() => {
    if (giftCardsInCart?.length > 0) {
      onApplyCertificateFromCart(
        giftCardsInCart[0].Amount,
        giftCardsInCart[0].GiftCertificateNumber
      );
    } else if (giftCardsInCart?.length === 0) {
      onUnapplyCertificate(true);
    }
  }, [giftCardsInCart]);

  return (
    <div className="custom-checkbox checkbox-lg">
      <input
        id="checkboxGift"
        type="checkbox"
        disabled={giftCardApplied}
        onClick={toggleApplyGiftCardFormHandler}
        ref={checkboxRef}
      />
      <label htmlFor="checkboxGift">I have a gift voucher</label>
      {showGiftCardForm && (
        <div className="row enter-gift-certificate">
          <div className="col-sm-6 col-md-4">
            <div className="form-group">
              <div className="flex-input">
                <input
                  className={`form-control show-placeholder ${giftCardError ? 'has-error' : ''}`}
                  id="giftCertificate"
                  placeholder="Enter gift voucher"
                  onChange={giftCardInputHandler}
                  value={giftCardInputValue}
                  disabled={isProcessing || giftCardApplied}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-apply"
                  disabled={!giftCardButtonEnabled || isProcessing || giftCardApplied}
                  onClick={submitGiftCardFormHandler}
                >
                  {isProcessing ? 'Processing...' : 'Apply code'}
                </button>
              </div>
              {giftCardError && <p className="error-message">{giftCardError}</p>}
            </div>
            {giftCardApplied && (
              <div className="form-group">
                <p>Great, we've applied your gift voucher {giftCardInputValue}.</p>
                <span className="bold">
                  <IconCross className="icon fill-blue" />
                </span>
                <a href="#" onClick={removeCertificateModalHandler}>
                  Remove Certificate
                </a>
              </div>
            )}
          </div>
        </div>
      )}
      <UnapplyGiftCertificatePrompt
        active={showRemoveCertificateModal}
        giftCertificate={removeGiftCertificateValues}
        onRequestClose={(giftCertificateRemoved) => {
          setShowRemoveCertificateModal(false);
          if (giftCertificateRemoved) {
            onUnapplyCertificate();
          }
        }}
        forceBackToCurrentPage
      />
    </div>
  );
};

PaymentGiftCertificate.propTypes = {
  isDonationOnly: PropTypes.bool,
  giftCardsInCart: PropTypes.array,
};
